import type { ShopperProducts, ShopperSearch } from '@/scapi';

export type ProductCardItem = ShopperProducts.schemas['Product'] | ShopperSearch.schemas['ProductSearchHit'];

type EnrichedProductSearchHit = ShopperSearch.schemas['ProductSearchHit'] & {
    c_brand?: unknown;
    c_custom?: {
        brand?: unknown;
    };
};

export const isProductSearchHit = (product: ProductCardItem): product is ShopperSearch.schemas['ProductSearchHit'] =>
    'productId' in product;

/** Build a card projection from a compact search hit without making an extra request per PLP tile. */
export const getProductFromGridItem = (item: ProductCardItem): ShopperProducts.schemas['Product'] => {
    if (!isProductSearchHit(item)) return item;

    const represented = (item.representedProduct ?? {}) as unknown as ShopperProducts.schemas['Product'];
    const enrichedItem = item as EnrichedProductSearchHit;
    const nestedCustomBrand = enrichedItem.c_custom?.brand;
    const customBrand = enrichedItem.c_brand;
    const hitInventory = item.inventory as { ats?: number } | undefined;
    const imageGroups =
        item.imageGroups ??
        represented.imageGroups ??
        (item.image
            ? [
                  {
                      viewType: 'large',
                      images: [item.image],
                  },
              ]
            : undefined);

    return {
        ...represented,
        id: item.productId,
        name: item.productName,
        brand:
            item.brand ??
            (typeof nestedCustomBrand === 'string' ? nestedCustomBrand : undefined) ??
            (typeof customBrand === 'string' ? customBrand : undefined) ??
            represented.brand,
        price: item.price ?? represented.price,
        priceMax: item.priceMax ?? represented.priceMax,
        currency: item.currency ?? represented.currency,
        imageGroups,
        inventory: {
            ...represented.inventory,
            ...hitInventory,
            orderable:
                item.inStock ?? (hitInventory?.ats == null ? represented.inventory?.orderable : hitInventory.ats > 0),
        },
    } as ShopperProducts.schemas['Product'];
};
